import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
} from '@mui/material';
import { People, Group, Event } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getPartners, addPartner } from './api';

const PartnerDash = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    partner_name: '',
    description: '',
    location: '',
    total_members: 0,
    tel_number: '',
    email: '',
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const partnersData = await getPartners();
        // Ensure data is an array
        const partnersArray = Array.isArray(partnersData) ? partnersData : (partnersData?.partners || partnersData?.data || []);
        setPartners(partnersArray);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalPartners = partners.length;

  // Calculate total activities
  const totalActivities = partners.reduce((sum, partner) => sum + (partner.activities?.length || 0), 0);

  // Calculate partner mothers count using mother_count
  const partnerMothersCount = partners.map(partner => ({
    id: partner.partner_id,
    name: partner.partner_name || `Partner ${partner.partner_id}`,
    motherCount: partner.mother_count || 0,
  }));

  // Determine partner with most mothers
  const partnerWithMostMothers = partnerMothersCount.reduce((max, curr) =>
    max.motherCount > curr.motherCount ? max : curr, { name: 'None', motherCount: 0 });

  // Calculate activities count per partner
  const partnerActivitiesCount = partners.map(partner => ({
    id: partner.partner_id,
    name: partner.partner_name || `Partner ${partner.partner_id}`,
    activityCount: (partner.activities || []).length,
  }));

  // Determine partner with most activities
  const partnerWithMostActivities = partnerActivitiesCount.reduce((max, curr) =>
    max.activityCount > curr.activityCount ? max : curr, { name: 'None', activityCount: 0 });

  // Prepare activities table data
  const activitiesData = partners
    .flatMap(partner =>
      (partner.activities || []).map(activity => ({
        activityName: activity.name || 'Unnamed Activity',
        partnerName: partner.partner_name || `Partner ${partner.partner_id}`,
        motherCount: partner.mother_count || 0,
        partnerId: partner.partner_id,
      }))
    )
    .sort((a, b) => b.motherCount - a.motherCount); // Sort by motherCount descending

  // Partners vs Mothers Chart
  const partnersVsMothersChart = {
    chart: { type: 'bar', height: 300 },
    title: { text: 'Partners vs. Total Mothers', style: { color: '#333', fontSize: '16px' } },
    xAxis: {
      categories: partnerMothersCount.map(p => p.name),
      labels: { style: { fontSize: '10px' } },
    },
    yAxis: { title: { text: 'Number of Mothers' }, allowDecimals: false },
    series: [{
      name: 'Mothers',
      data: partnerMothersCount.map(p => p.motherCount),
      color: '#ff6f61',
    }],
    credits: { enabled: false },
    tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
    plotOptions: { bar: { borderColor: '#e63946', borderWidth: 1 } },
  };

  // Monthly Activities Trend Chart
  const monthlyActivitiesTrendChart = (() => {
    // Generate last 6 months
    const months = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      return date.toLocaleString('default', { month: 'short', year: 'numeric' });
    });

    // Map activities to months
    const activitiesByPartnerMonth = {};
    partners.forEach(partner => {
      activitiesByPartnerMonth[partner.partner_id] = {};
      months.forEach(month => {
        activitiesByPartnerMonth[partner.partner_id][month] = 0;
      });
      (partner.activities || []).forEach(activity => {
        // Use activity.created_at or partner.created_at as fallback
        const activityDate = activity.created_at || partner.created_at;
        if (activityDate) {
          const date = new Date(activityDate);
          const monthKey = date.toLocaleString('default', { month: 'short', year: 'numeric' });
          if (months.includes(monthKey)) {
            activitiesByPartnerMonth[partner.partner_id][monthKey]++;
          }
        }
      });
    });

    return {
      chart: { type: 'line', height: 300 },
      title: { text: 'Monthly Activities per Partner', style: { color: '#333', fontSize: '16px' } },
      xAxis: {
        categories: months,
        labels: { style: { fontSize: '10px' } },
      },
      yAxis: { title: { text: 'Number of Activities' }, allowDecimals: false },
      series: partnerMothersCount.map(p => ({
        name: p.name,
        data: months.map(month => activitiesByPartnerMonth[p.id][month] || 0),
      })),
      credits: { enabled: false },
      tooltip: { backgroundColor: '#1d3557', style: { color: '#fff' } },
      plotOptions: { line: { marker: { enabled: true, radius: 3 } } },
    };
  })();

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActivityChangePage = (event, newPage) => setActivityPage(newPage);
  const handleActivityChangeRowsPerPage = (event) => {
    setActivityRowsPerPage(parseInt(event.target.value, 10));
    setActivityPage(0);
  };

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      partner_name: '',
      description: '',
      location: '',
      total_members: 0,
      tel_number: '',
      email: '',
    });
    setFormError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'total_members' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleAddPartner = async () => {
    if (!formData.partner_name.trim()) {
      setFormError('Partner name is required.');
      return;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (formData.tel_number && !/^\+?\d{10,15}$/.test(formData.tel_number)) {
      setFormError('Please enter a valid phone number (10-15 digits, optional +).');
      return;
    }
    setLoading(true);
    try {
      const response = await addPartner({
        partner_name: formData.partner_name,
        description: formData.description,
        location: formData.location,
        total_members: formData.total_members,
        tel_number: formData.tel_number,
        email: formData.email,
      });
      setPartners(prev => [...prev, response]);
      handleCloseDialog();
    } catch (error) {
      setError(`Failed to add partner: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, background: '#f5f5f5', minHeight: '100vh', width: '100%' }}>
      

      <Box sx={{ mb: 4, p:4,display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            '&:hover': { backgroundColor: theme.palette.primary.dark },
            borderRadius: 2,
            textTransform: 'none',
            padding: '8px 16px',
            transition: 'all 0.3s ease',
          }}
        >
          Add Partner
        </Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {!loading && !error && (
        <>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 4, 
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <StatCard
              icon={<Group sx={{ fontSize: 40, opacity: 0.8 }} />}
              title="Total Partners"
              value={totalPartners}
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description="Registered organizations"
            />
            <StatCard
              icon={<People sx={{ fontSize: 40, opacity: 0.8 }} />}
              title="Most Mothers"
              value={partnerWithMostMothers.motherCount}
              color="linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)"
              description={`${partnerWithMostMothers.name}`}
            />
            <StatCard
              icon={<Event sx={{ fontSize: 40, opacity: 0.8 }} />}
              title="Most Activities"
              value={partnerWithMostActivities.activityCount}
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description={`${partnerWithMostActivities.name}`}
            />
            <StatCard
              icon={<Event sx={{ fontSize: 40, opacity: 0.8 }} />}
              title="Total Activities"
              value={totalActivities}
              color="linear-gradient(135deg, #f92955 0%, #d41e3f 100%)"
              description="Across all partners"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'nowrap',
              width: '100%',
              overflowX: 'auto',
              mb: 4,
            }}
          >
            <Paper sx={{ ...chartPaperStyle, flex: 1, minWidth: 0 }}>
              <HighchartsReact highcharts={Highcharts} options={partnersVsMothersChart} />
            </Paper>
            <Paper sx={{ ...chartPaperStyle, flex: 1, minWidth: 0 }}>
              <HighchartsReact highcharts={Highcharts} options={monthlyActivitiesTrendChart} />
            </Paper>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 4,
              width: '100%',
            }}
          >
            {/* Partners Table */}
            <Box sx={{ flex: 1 }}>
              <Paper sx={{ p: 3, borderRadius: 2, background: '#f92955', color: '#FFFFFF' }}>
                <Typography variant="h6" sx={{ color: '#333', mb: 2 }}>
                  Partners
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><Group sx={{ verticalAlign: 'middle', mr: 1 }} />Partner</TableCell>
                      <TableCell><Event sx={{ verticalAlign: 'middle', mr: 1 }} />Location</TableCell>
                      <TableCell><People sx={{ verticalAlign: 'middle', mr: 1 }} />Contact</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(partner => (
                      <TableRow key={partner.partner_id}>
                        <TableCell>{partner.partner_name || 'N/A'}</TableCell>
                        <TableCell>{partner.location || 'N/A'}</TableCell>
                        <TableCell>{partner.tel_number || partner.email || 'N/A'}</TableCell>
                        <TableCell>{partner.description || 'No description'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={partners.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Box>

            {/* Activities Table */}
            <Box sx={{ flex: 1 }}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: '#333', mb: 2 }}>
                  Activities
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><Event sx={{ verticalAlign: 'middle', mr: 1 }} />Activity</TableCell>
                      <TableCell><Group sx={{ verticalAlign: 'middle', mr: 1 }} />Partner</TableCell>
                      <TableCell><People sx={{ verticalAlign: 'middle', mr: 1 }} />Mothers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activitiesData.slice(activityPage * activityRowsPerPage, activityPage * activityRowsPerPage + activityRowsPerPage).map((activity, index) => (
                      <TableRow key={`${activity.partnerId}-${activity.activityName}-${index}`}>
                        <TableCell>{activity.activityName}</TableCell>
                        <TableCell>{activity.partnerName}</TableCell>
                        <TableCell>{activity.motherCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={activitiesData.length}
                  rowsPerPage={activityRowsPerPage}
                  page={activityPage}
                  onPageChange={handleActivityChangePage}
                  onRowsPerPageChange={handleActivityChangeRowsPerPage}
                />
              </Paper>
            </Box>
          </Box>

          <Dialog open={openDialog} onClose={handleCloseDialog}>
            <DialogTitle sx={{ color: '#333', fontWeight: 'bold' }}>Add New Partner</DialogTitle>
            <DialogContent>
              {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
              <TextField
                autoFocus
                margin="dense"
                name="partner_name"
                label="Partner Name"
                type="text"
                fullWidth
                value={formData.partner_name}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="description"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="location"
                label="Location"
                type="text"
                fullWidth
                value={formData.location}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="total_members"
                label="Total Members"
                type="number"
                fullWidth
                value={formData.total_members}
                onChange={handleInputChange}
                inputProps={{ min: 0 }}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="tel_number"
                label="Phone Number"
                type="text"
                fullWidth
                value={formData.tel_number}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                name="email"
                label="Email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} sx={{ color: '#555' }}>Cancel</Button>
              <Button
                onClick={handleAddPartner}
                variant="contained"
                sx={{ backgroundColor: theme.palette.primary.main, color: '#fff', '&:hover': { backgroundColor: theme.palette.primary.dark } }}
              >
                Add Partner
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

// Updated StatCard matching Mothers component style
const StatCard = ({ icon, title, value, color, description }) => {
  return (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(255,105,180,0.12)',
      background: color,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      flex: '1 1 250px',
      minWidth: '250px',
      maxWidth: '100%',
    }}>
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.1)',
      }} />
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
            {value}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const chartPaperStyle = {
  p: 2,
  borderRadius: 3,
  boxShadow: '0 6px 30px rgba(0,0,0,0.08)',
  background: 'linear-gradient(to bottom right, #ffffff, #f8fafc)',
  height: '100%',
  width: '100%',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 36px rgba(0,0,0,0.15)',
  },
};

export default PartnerDash;